// Chat.js (improved demo UX)
// Expert: Senior Product/UX & Frontend Engineer
// Changes:
// - Friendly intro first, then short PD consent
// - Typing indicator between bot messages
// - Clear CTA to open real Telegram bot after consent
// - Brighter buttons and restart flow
// - Keeps /qr command + URL normalization + QR with transparent bg

import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { motion } from 'framer-motion'
import { IoSend } from 'react-icons/io5'
import { botLink } from './BotLink'
import StatusBar from './StatusBar'
import Bubble from './Bubble'
import QRCode from 'qrcode'

// Autoconsent intentionally disabled
const AUTO_CONSENT = false

export default function Chat({ className }) {
  // --- state ---
  const [showChat, setShowChat] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [messages, setMessages] = useState([]) // items: {isLeft:boolean, type?:'consent'|'typing', content:string}
  const [isGlowing, setIsGlowing] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [consentShown, setConsentShown] = useState(false)

  const startedRef = useRef(false)
  const containerRef = useRef(null)

  // --- helpers: URL/QR ---
  const isLikelyHostname = s => {
    if (!s || typeof s !== 'string') return false
    const cleaned = s.replace(/^[a-z]+:\/\//i, '').replace(/\/.*$/, '')
    if (!cleaned.includes('.') || /\s/.test(cleaned)) return false
    if (cleaned.startsWith('-') || cleaned.endsWith('.')) return false
    try {
      new URL(`https://${cleaned}`)
      return true
    } catch {
      return false
    }
  }

  const normalizeToUrl = raw => {
    const s = (raw || '').trim()
    if (!s) return null
    if (/^[a-z]+:\/\//i.test(s)) {
      try {
        new URL(s)
        return s
      } catch {
        return null
      }
    }
    if (s.startsWith('/')) return null
    if (isLikelyHostname(s)) {
      try {
        return new URL(`https://${s}`).toString()
      } catch {
        return null
      }
    }
    return null
  }

  const makeQrDataUrl = async text => {
    return QRCode.toDataURL(text, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 512,
      scale: 4,
      color: { dark: '#000000', light: '#00000000' }, // transparent background
    })
  }

  const pushMessages = arr => setMessages(prev => [...prev, ...arr])

  // --- animations ---
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: { opacity: 1, transition: { staggerChildren: 0.25 } },
  }
  const bubbleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  // --- typing indicator ---
  const showTyping = async (ms = 900) => {
    const id = `typing-${Date.now()}`
    setMessages(prev => [
      ...prev,
      { isLeft: true, type: 'typing', content: id },
    ])
    await new Promise(r => setTimeout(r, ms))
    setMessages(prev =>
      prev.filter(m => !(m.type === 'typing' && m.content === id)),
    )
  }

  const removeConsentBubble = () => {
    setMessages(prev => prev.filter(m => m.type !== 'consent'))
    setConsentShown(false)
  }

  // --- start scenario: intro -> PD -> consent buttons ---
  useLayoutEffect(() => {
    if (!showChat || startedRef.current) return
    startedRef.current = true

    let cancelled = false
    const wait = ms => new Promise(r => setTimeout(r, ms))

    ;(async () => {
      if (cancelled) return
      // 1) Friendly intro
      pushMessages([
        {
          isLeft: true,
          content:
            '👋 Привет! Я Selfiebot. На событиях я рассылаю гостям их фото 📸. Хочешь увидеть, как это работает?',
        },
      ])

      await showTyping(700)
      if (cancelled) return

      // 2) Short PD text
      pushMessages([
        {
          isLeft: true,
          content:
            'Я спрошу твоё согласие на обработку персональных данных. Подробнее: <a href="https://selfiebot.ru/pd" target="_blank" class="text-blue-500 underline">selfiebot.ru/pd</a>',
        },
      ])

      await wait(150)
      if (cancelled) return

      // 3) Consent buttons
      pushMessages([
        {
          isLeft: true,
          type: 'consent',
          content: `
            <div class="flex gap-2">
              <button id="pd-agree" class="px-3 py-2 bg-[#2ecc71] text-white rounded-md shadow">Согласен</button>
              <button id="pd-decline" class="px-3 py-2 bg-gray-300 text-gray-900 rounded-md">Не согласен</button>
            </div>
          `,
        },
      ])
      setConsentShown(true)
    })()

    return () => {
      cancelled = true
    }
  }, [showChat])

  // --- delegated clicks: consent + restart + open-bot ---
  useEffect(() => {
    if (!showChat) return
    const root = containerRef.current
    if (!root) return

    const onClick = e => {
      const t = e.target
      if (!(t instanceof HTMLElement)) return
      if (t.id === 'pd-agree') {
        handleConsent(true)
      } else if (t.id === 'pd-decline') {
        handleConsent(false)
      } else if (t.id === 'demo-restart') {
        handleResetChat()
        setTimeout(() => handleStartClick(), 50)
      } else if (t.id === 'open-real-bot') {
        window.open(botLink, '_blank', 'noopener,noreferrer')
      }
    }

    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [showChat])

  // --- optional auto-consent ---
  useEffect(() => {
    if (!consentShown || !AUTO_CONSENT) return
    const t = setTimeout(() => handleConsent(true), 150)
    return () => clearTimeout(t)
  }, [consentShown])

  // --- consent logic ---
  const handleConsent = async agree => {
    removeConsentBubble()
    if (agree) {
      pushMessages([{ isLeft: false, content: 'Согласен' }])

      await showTyping(700)
      pushMessages([
        {
          isLeft: true,
          content:
            'Отлично! На реальном событии я собираю контакты гостей и отправляю им их фото, как только фотограф их загрузит.',
        },
      ])

      await showTyping(700)
      pushMessages([
        {
          isLeft: true,
          content: 'Хочешь попробовать настоящий бот прямо в Telegram?',
        },
        {
          isLeft: true,
          content: `
            <div class="mt-2">
              <button id="open-real-bot" class="px-4 py-2 bg-[#0ea5e9] text-white rounded-md shadow hover:shadow-md">Перейти в Telegram</button>
            </div>
          `,
        },
      ])
    } else {
      pushMessages([{ isLeft: false, content: 'Не согласен' }])
      await showTyping(600)
      pushMessages([
        {
          isLeft: true,
          content:
            'Понимаю. Без согласия сервис недоступен. Политику можно прочитать на <a href="https://selfiebot.ru/pd" target="_blank" class="text-blue-500 underline">selfiebot.ru/pd</a>.',
        },
        {
          isLeft: true,
          content:
            '<div class="mt-2"><button id="demo-restart" class="px-4 py-2 bg-blue-600 text-white rounded-md shadow">Перезапустить демо</button></div>',
        },
      ])
    }
  }

  // --- input / sending ---
  const handleInputChange = e => setInputValue(e.target.value)

  const handleSendMessage = async () => {
    const raw = inputValue.trim()
    if (!raw || isBusy) return

    setIsBusy(true)
    const userMsg = { isLeft: false, content: raw }

    // /qr
    if (/^\/?qr(?:\s+.*)?$/i.test(raw)) {
      const arg = raw.replace(/^\/?qr/i, '').trim()

      if (!arg) {
        pushMessages([
          userMsg,
          {
            isLeft: true,
            content:
              'Использование: <code>/qr example.com</code> или <code>/qr https://example.com/path</code>',
          },
        ])
        setInputValue('')
        setIsBusy(false)
        return
      }

      const normalized = normalizeToUrl(arg)
      if (!normalized) {
        pushMessages([
          userMsg,
          {
            isLeft: true,
            content:
              'Неверный адрес. Введите домен вида <code>example.com</code> или полный URL <code>https://example.com/page</code>.',
          },
        ])
        setInputValue('')
        setIsBusy(false)
        return
      }

      try {
        const dataUrl = await makeQrDataUrl(normalized)
        pushMessages([
          userMsg,
          {
            isLeft: true,
            content: `QR-код для <a href="${normalized}" target="_blank" class="text-blue-500 underline">${normalized}</a>`,
          },
          {
            isLeft: true,
            content: `
              <div class="inline-block p-2 rounded-lg border border-gray-300 bg-white">
                <div
                  class="w-[190px] h-[190px] bg-gradient-to-br from-orange-600 to-violet-600 rounded-md"
                  style="
                    -webkit-mask: url('${dataUrl}') no-repeat center / contain;
                    mask: url('${dataUrl}') no-repeat center / contain;
                  ">
                </div>
              </div>
            `,
          },
        ])
      } catch {
        pushMessages([
          userMsg,
          {
            isLeft: true,
            content: 'Не удалось сгенерировать QR. Попробуйте другой адрес.',
          },
        ])
      } finally {
        setInputValue('')
        setIsBusy(false)
      }
      return
    }

    // Default answer
    const botAnswer = {
      isLeft: true,
      content:
        'Этот демо-бот командам не обучен. Зато есть рабочий: <br /><a href="' +
        botLink +
        '" target="_blank" class="text-blue-500 underline">t.me/leadrubot</a>.',
    }
    pushMessages([userMsg, botAnswer])
    setInputValue('')
    setIsBusy(false)
  }

  // --- UI: reset/start ---
  const handleResetChat = () => {
    setShowChat(false)
    setMessages([])
    setInputValue('')
    setIsGlowing(false)
    setIsBusy(false)
    setConsentShown(false)
    startedRef.current = false
  }

  const handleStartClick = () => {
    setShowChat(true)
    setIsGlowing(true)
    setTimeout(() => setIsGlowing(false), 2000)
  }

  // Autoscroll to bottom on new messages
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages])

  // --- render ---
  return (
    <div
      className={`relative w-[320px] h-[640px] bg-black rounded-[40px] border-4 border-gray-800 shadow-2xl overflow-hidden transition-shadow duration-500 ${
        isGlowing
          ? 'shadow-[0_0_160px_80px_rgba(255,255,255,0.9)]'
          : 'shadow-[0_0_160px_80px_rgba(255,255,255,0.2)]'
      }`}
    >
      <div
        className="absolute top-[5px] bottom-[5px] left-[5px] right-[5px]
  rounded-[30px] overflow-hidden bg-gray-300 bg-telegram bg-blend-overlay"
      >
        <div className="pt-[10px] px-4">
          <StatusBar />
        </div>

        {showChat ? (
          <motion.div
            className={`flex flex-col space-y-3 ${className} pb-16`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            viewport={{ once: true, amount: 'all' }}
            ref={containerRef}
            style={{ overflowY: 'auto', maxHeight: 'calc(100% - 50px)' }}
          >
            <div className="h-[60px]">&nbsp;</div>

            {messages.map((msg, index) => (
              <motion.div key={index} variants={bubbleVariants}>
                <Bubble isLeft={msg.isLeft}>
                  {msg.type === 'typing'
                    ? '<span class="inline-flex gap-1"><span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></span><span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse [animation-delay:150ms]"></span><span class="w-2 h-2 rounded-full bg-gray-400 animate-pulse [animation-delay:300ms]"></span></span>'
                    : msg.content}
                </Bubble>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="flex items-center justify-center h-full text-center text-gray-600">
            <div>
              Это живой демо-бот. Нажмите
              <br />
              <span
                className="shimmer font-bold text-blue-500 tracking-[0.05rem] hover:tracking-[0.25rem] transition-all duration-300 cursor-pointer"
                onClick={handleStartClick}
              >
                START
              </span>
            </div>
          </div>
        )}
      </div>

      <div
        className="absolute top-[15px] left-1/2 transform -translate-x-1/2 w-[80px] h-[25px] bg-gray-800 rounded-xl cursor-pointer"
        onClick={handleResetChat}
        title="Сбросить демо"
      />

      {!showChat && (
        <button
          className="absolute bottom-[5px] left-[5px] right-[5px] bg-blue-500 p-3 text-white rounded-b-[25px] text-xs tracking-[0.05rem] hover:tracking-[0.25rem] hover:font-semibold transition-all duration-300"
          onClick={handleStartClick}
          aria-label="Начать демо"
        >
          START
        </button>
      )}

      {showChat && (
        <div className="absolute bottom-[5px] left-[5px] right-[5px] flex items-center bg-gray-100 p-3 rounded-b-[25px] text-xs">
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
            className="flex-1 text-gray-800 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите сообщение..."
            aria-label="Сообщение"
          />
          &nbsp;&nbsp;
          <button
            className="text-blue-500 disabled:opacity-50"
            onClick={handleSendMessage}
            disabled={isBusy}
            title={isBusy ? 'Подождите…' : 'Отправить'}
          >
            <IoSend size={24} />
          </button>
        </div>
      )}
    </div>
  )
}
